import dayjs from "dayjs";
import { FC } from "react";
import {
  IOperatorInvoiceCancellation,
  IOperatorInvoicePreviewItem,
  OperatorInvoiceAdjustmentReason
} from "../../interfaces/endpoints/IOperatorInvoice";
import Pagination from "../ui/table/Pagination";
import { PREVIEW_PAGE_SIZE, usePaged } from "./usePaged";

const formatMoney = (value: number) => `${(value ?? 0).toFixed(2)} EUR`;

const reasonLabel: Record<OperatorInvoiceAdjustmentReason, string> = {
  [OperatorInvoiceAdjustmentReason.UserCancellation]: "User cancellation",
  [OperatorInvoiceAdjustmentReason.AdminReservationCancellation]:
    "Admin reservation cancellation",
  [OperatorInvoiceAdjustmentReason.AdminTicketCancellation]:
    "Admin ticket cancellation",
  [OperatorInvoiceAdjustmentReason.OperatorSwitchAway]: "Operator switch away",
  [OperatorInvoiceAdjustmentReason.OperatorSwitchTo]: "Operator switch to",
  [OperatorInvoiceAdjustmentReason.InvoiceOnlyCancel]: "Invoice-only cancel"
};

export const ReservationRowsTable: FC<{
  items: IOperatorInvoicePreviewItem[];
  // Adds an Operator column when the preview spans more than one operator.
  showOperator?: boolean;
}> = ({ items, showOperator = false }) => {
  const { page, pageItems, onPageChange } = usePaged(items);

  return (
    <div className="overflow-auto border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            {showOperator && <th className="text-left p-2">Operatori</th>}
            <th className="text-left p-2">Line</th>
            <th className="text-left p-2">Journey date</th>
            <th className="text-left p-2">Payment date</th>
            <th className="text-right p-2">Pax</th>
            <th className="text-right p-2">Total</th>
            <th className="text-right p-2">Commission</th>
            <th className="text-right p-2">Operator</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((row) => (
            <tr
              key={`${row.operatorId}-${row.journeyReservationId}`}
              className="border-t hover:bg-gray-50"
            >
              {showOperator && <td className="p-2">{row.operatorName}</td>}
              <td className="p-2">{row.lineName}</td>
              <td className="p-2">{dayjs(row.journeyDate).format("DD-MM-YY")}</td>
              <td className="p-2">{dayjs(row.paymentDate).format("DD-MM-YY")}</td>
              <td className="p-2 text-right">{row.passengerCount}</td>
              <td className="p-2 text-right">{formatMoney(row.totalPrice)}</td>
              <td className="p-2 text-right">
                {formatMoney(row.commissionAmount)}{" "}
                <span className="text-xs text-gray-400">
                  ({Math.round(row.commissionRate * 100)}%)
                </span>
              </td>
              <td className="p-2 text-right font-medium">
                {formatMoney(row.operatorAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        totalCount={items.length}
        pageSize={PREVIEW_PAGE_SIZE}
        page={page}
        length={pageItems.length}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export const CancellationsTable: FC<{
  cancellations: IOperatorInvoiceCancellation[];
  // Adds an Operator column when the preview spans more than one operator.
  showOperator?: boolean;
}> = ({ cancellations, showOperator = false }) => {
  const { page, pageItems, onPageChange } = usePaged(cancellations);

  return (
    <div className="overflow-auto border rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            {showOperator && <th className="text-left p-2">Operatori</th>}
            <th className="text-left p-2">Ticket</th>
            <th className="text-left p-2">Journey</th>
            <th className="text-left p-2">Reason</th>
            <th className="text-left p-2">Event date</th>
            <th className="text-right p-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((row) => (
            <tr key={row.adjustmentId} className="border-t hover:bg-gray-50">
              {showOperator && <td className="p-2">{row.operatorName}</td>}
              <td className="p-2">#{row.ticketId}</td>
              <td className="p-2">{row.uniqueJourneyId}</td>
              <td className="p-2">{reasonLabel[row.reason] ?? "—"}</td>
              <td className="p-2">
                {dayjs(row.eventDateUtc).format("DD-MM-YY HH:mm")}
              </td>
              <td className="p-2 text-right">{formatMoney(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        totalCount={cancellations.length}
        pageSize={PREVIEW_PAGE_SIZE}
        page={page}
        length={pageItems.length}
        onPageChange={onPageChange}
      />
    </div>
  );
};
